import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, Users, Baby, CreditCard, TrendingUp, Activity, PieChart, Clock, Bell } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, Legend, AreaChart, Area, LineChart, Line,
} from "recharts";
import { format, subMonths, startOfMonth, endOfMonth, formatDistanceToNow } from "date-fns";
import { ar } from "date-fns/locale";
import { useAuth } from "@/contexts/AuthContext";

const COLORS = ["hsl(217, 91%, 60%)", "hsl(142, 71%, 45%)", "hsl(280, 67%, 55%)", "hsl(38, 92%, 50%)", "hsl(0, 84%, 60%)"];

const AdminDashboard = () => {
  const { user } = useAuth();
  const { data: stats, isLoading } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => {
      const [nurseries, profiles, children, subscriptions] = await Promise.all([
        supabase.from("nurseries").select("id", { count: "exact", head: true }),
        supabase.from("profiles").select("id", { count: "exact", head: true }),
        supabase.from("children").select("id", { count: "exact", head: true }),
        supabase.from("nursery_subscriptions").select("*"),
      ]);

      const subs = subscriptions.data || [];
      const activeSubs = subs.filter((s) => s.status === "active");
      const totalRevenue = subs.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);

      return {
        nurseries: nurseries.count || 0,
        users: profiles.count || 0,
        children: children.count || 0,
        activeSubscriptions: activeSubs.length,
        totalRevenue,
      };
    },
  });

  // Monthly growth data
  const { data: growthData } = useQuery({
    queryKey: ["admin-growth"],
    queryFn: async () => {
      const months = Array.from({ length: 6 }, (_, i) => {
        const date = subMonths(new Date(), 5 - i);
        return {
          start: startOfMonth(date).toISOString(),
          end: endOfMonth(date).toISOString(),
          label: format(date, "MMM yyyy", { locale: ar }),
        };
      });

      const [nurseriesAll, childrenAll, profilesAll] = await Promise.all([
        supabase.from("nurseries").select("created_at"),
        supabase.from("children").select("created_at"),
        supabase.from("profiles").select("created_at"),
      ]);

      return months.map((m) => ({
        month: m.label,
        nurseries: nurseriesAll.data?.filter((n) => n.created_at >= m.start && n.created_at <= m.end).length || 0,
        children: childrenAll.data?.filter((c) => c.created_at >= m.start && c.created_at <= m.end).length || 0,
        users: profilesAll.data?.filter((p) => p.created_at >= m.start && p.created_at <= m.end).length || 0,
      }));
    },
  });

  // Subscription distribution
  const { data: subDistribution } = useQuery({
    queryKey: ["admin-sub-distribution"],
    queryFn: async () => {
      const { data } = await supabase.from("nursery_subscriptions").select("plan, status");
      if (!data?.length) return [];

      const planCounts: Record<string, number> = {};
      data.forEach((s) => {
        const label = s.plan === "free" ? "مجاني" : s.plan === "basic" ? "أساسي" : s.plan === "premium" ? "مميز" : s.plan === "enterprise" ? "مؤسسات" : s.plan;
        planCounts[label] = (planCounts[label] || 0) + 1;
      });

      return Object.entries(planCounts).map(([name, value]) => ({ name, value }));
    },
  });

  // Subscription status distribution
  const { data: statusDistribution } = useQuery({
    queryKey: ["admin-status-distribution"],
    queryFn: async () => {
      const { data } = await supabase.from("nursery_subscriptions").select("status");
      if (!data?.length) return [];

      const statusLabels: Record<string, string> = { active: "نشط", expired: "منتهي", suspended: "معلق", cancelled: "ملغي" };
      const counts: Record<string, number> = {};
      data.forEach((s) => {
        const label = statusLabels[s.status] || s.status;
        counts[label] = (counts[label] || 0) + 1;
      });

      return Object.entries(counts).map(([name, value]) => ({ name, value }));
    },
  });

  // Revenue by month
  const { data: revenueData } = useQuery({
    queryKey: ["admin-revenue"],
    queryFn: async () => {
      const { data } = await supabase.from("nursery_subscriptions").select("amount, started_at, billing_cycle");
      if (!data?.length) return [];

      const months = Array.from({ length: 6 }, (_, i) => {
        const date = subMonths(new Date(), 5 - i);
        return {
          start: startOfMonth(date).toISOString(),
          end: endOfMonth(date).toISOString(),
          label: format(date, "MMM", { locale: ar }),
        };
      });

      return months.map((m) => ({
        month: m.label,
        revenue: data
          .filter((s) => s.started_at >= m.start && s.started_at <= m.end)
          .reduce((sum, s) => sum + (Number(s.amount) || 0), 0),
      }));
    },
  });

  // Top nurseries by children count
  const { data: topNurseries } = useQuery({
    queryKey: ["admin-top-nurseries"],
    queryFn: async () => {
      const { data: children } = await supabase.from("children").select("nursery_id");
      const { data: nurseries } = await supabase.from("nurseries").select("id, name");
      if (!children?.length || !nurseries?.length) return [];

      const counts: Record<string, number> = {};
      children.forEach((c) => { counts[c.nursery_id] = (counts[c.nursery_id] || 0) + 1; });

      return nurseries
        .map((n) => ({ name: n.name, children: counts[n.id] || 0 }))
        .sort((a, b) => b.children - a.children)
        .slice(0, 8);
    },
  });

  // Role distribution
  const { data: roleData } = useQuery({
    queryKey: ["admin-role-distribution"],
    queryFn: async () => {
      const { data } = await supabase.from("user_roles").select("role");
      if (!data?.length) return [];

      const roleLabels: Record<string, string> = { admin: "مدير", teacher: "معلم", parent: "ولي أمر", super_admin: "مشرف عام" };
      const counts: Record<string, number> = {};
      data.forEach((r) => {
        const label = roleLabels[r.role] || r.role;
        counts[label] = (counts[label] || 0) + 1;
      });

      return Object.entries(counts).map(([name, value]) => ({ name, value }));
    },
  });

  // Latest admin notifications (activity feed)
  const { data: activityFeed } = useQuery({
    queryKey: ["admin-activity-feed", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user!.id)
        .in("type", [
          "admin_new_nursery", "admin_subscription_change", "admin_new_ticket",
          "admin_new_user", "admin_subscription_request", "broadcast",
        ])
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
    refetchInterval: 30000,
  });

  const activityTypeConfig: Record<string, { icon: string; color: string }> = {
    admin_new_nursery: { icon: "🏫", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
    admin_subscription_change: { icon: "💳", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" },
    admin_new_ticket: { icon: "🎫", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" },
    admin_new_user: { icon: "👤", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" },
    admin_subscription_request: { icon: "📩", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300" },
    broadcast: { icon: "📢", color: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300" },
  };

  const statCards = [
    { title: "إجمالي الحضانات", value: stats?.nurseries, icon: Building2, color: "text-blue-500" },
    { title: "إجمالي المستخدمين", value: stats?.users, icon: Users, color: "text-green-500" },
    { title: "إجمالي الأطفال", value: stats?.children, icon: Baby, color: "text-purple-500" },
    { title: "الاشتراكات النشطة", value: stats?.activeSubscriptions, icon: CreditCard, color: "text-amber-500" },
    { title: "إجمالي الإيرادات", value: stats?.totalRevenue ? `${stats.totalRevenue} ج.م` : "0 ج.م", icon: TrendingUp, color: "text-emerald-500" },
  ];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-popover border rounded-lg shadow-lg p-3 text-sm">
        <p className="font-medium mb-1">{label}</p>
        {payload.map((p: any, i: number) => (
          <p key={i} style={{ color: p.color }} className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: p.color }} />
            {p.name}: <span className="font-semibold">{p.value}</span>
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h1 className="text-2xl font-bold">لوحة التحكم العامة</h1>
        <p className="text-muted-foreground text-sm">نظرة شاملة على المنصة</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {statCards.map((card) => (
          <Card key={card.title}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{card.title}</CardTitle>
              <card.icon className={`w-5 h-5 ${card.color}`} />
            </CardHeader>
            <CardContent>
              {isLoading ? <Skeleton className="h-8 w-20" /> : <p className="text-2xl font-bold">{card.value}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Growth */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="w-5 h-5 text-blue-500" />
              نمو المنصة الشهري
            </CardTitle>
          </CardHeader>
          <CardContent>
            {growthData?.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={growthData} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="nurseries" name="حضانات" fill="hsl(217, 91%, 60%)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="users" name="مستخدمين" fill="hsl(142, 71%, 45%)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="children" name="أطفال" fill="hsl(280, 67%, 55%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center py-12 text-muted-foreground">لا توجد بيانات كافية</p>
            )}
          </CardContent>
        </Card>

        {/* Revenue Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              الإيرادات الشهرية (ج.م)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {revenueData?.some((r) => r.revenue > 0) ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(142, 71%, 45%)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(142, 71%, 45%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="revenue" name="الإيرادات" stroke="hsl(142, 71%, 45%)" fill="url(#revenueGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center py-12 text-muted-foreground">لا توجد إيرادات مسجلة بعد</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Subscription Plans */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <PieChart className="w-5 h-5 text-amber-500" />
              توزيع خطط الاشتراك
            </CardTitle>
          </CardHeader>
          <CardContent>
            {subDistribution?.length ? (
              <ResponsiveContainer width="100%" height={250}>
                <RechartsPie>
                  <Pie
                    data={subDistribution}
                    cx="50%" cy="50%"
                    innerRadius={55} outerRadius={85}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {subDistribution.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </RechartsPie>
              </ResponsiveContainer>
            ) : (
              <p className="text-center py-12 text-muted-foreground">لا توجد اشتراكات</p>
            )}
          </CardContent>
        </Card>

        {/* Role Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="w-5 h-5 text-blue-500" />
              توزيع أدوار المستخدمين
            </CardTitle>
          </CardHeader>
          <CardContent>
            {roleData?.length ? (
              <ResponsiveContainer width="100%" height={250}>
                <RechartsPie>
                  <Pie
                    data={roleData}
                    cx="50%" cy="50%"
                    innerRadius={55} outerRadius={85}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {roleData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </RechartsPie>
              </ResponsiveContainer>
            ) : (
              <p className="text-center py-12 text-muted-foreground">لا توجد بيانات</p>
            )}
          </CardContent>
        </Card>

        {/* Subscription Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CreditCard className="w-5 h-5 text-green-500" />
              حالات الاشتراكات
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statusDistribution?.length ? (
              <ResponsiveContainer width="100%" height={250}>
                <RechartsPie>
                  <Pie
                    data={statusDistribution}
                    cx="50%" cy="50%"
                    innerRadius={55} outerRadius={85}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {statusDistribution.map((_, i) => (
                      <Cell key={i} fill={["hsl(142, 71%, 45%)", "hsl(0, 84%, 60%)", "hsl(38, 92%, 50%)", "hsl(220, 9%, 60%)"][i % 4]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </RechartsPie>
              </ResponsiveContainer>
            ) : (
              <p className="text-center py-12 text-muted-foreground">لا توجد اشتراكات</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Nurseries */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="w-5 h-5 text-purple-500" />
            أكبر الحضانات حسب عدد الأطفال
          </CardTitle>
        </CardHeader>
        <CardContent>
          {topNurseries?.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topNurseries} layout="vertical" margin={{ right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={120} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="children" name="عدد الأطفال" fill="hsl(280, 67%, 55%)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center py-12 text-muted-foreground">لا توجد بيانات كافية</p>
          )}
        </CardContent>
      </Card>

      {/* Activity Feed */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Bell className="w-5 h-5 text-primary" />
            آخر التحديثات والأحداث
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activityFeed?.length ? (
            <ScrollArea className="max-h-[400px]">
              <div className="space-y-3">
                {activityFeed.map((item: any) => {
                  const config = activityTypeConfig[item.type] || { icon: "🔔", color: "bg-muted text-muted-foreground" };
                  return (
                    <div key={item.id} className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
                      <span className={`text-lg w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${config.color}`}>
                        {config.icon}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{item.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{item.body}</p>
                        <p className="text-[10px] text-muted-foreground/60 mt-1 flex items-center gap-1">
                          <Clock className="w-2.5 h-2.5" />
                          {formatDistanceToNow(new Date(item.created_at), { addSuffix: true, locale: ar })}
                        </p>
                      </div>
                      {!item.is_read && (
                        <Badge variant="secondary" className="text-[10px] shrink-0">جديد</Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          ) : (
            <p className="text-center py-12 text-muted-foreground">لا توجد تحديثات بعد</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
